import assign from 'lodash/assign';

// Constellation
import { Box, Checkbox, FieldSet, Label, LabeledControl, Legend, VisuallyHidden } from '@zillow/constellation-10';

// Components
import FilterWrapper from '../FilterWrapper';

// Flux
import { useAppSelector } from '../../flux/configureStore';

// Misc / Utils
import { useOptimisticReducer } from '../../utils/hooks';

// Types
import type { amenitiesType, laundryType, PartialFilterType, petsType } from '../../models/Types';

const Amenities = ({ onFilterChange }: { onFilterChange: (arg0: PartialFilterType) => void }) => {
  const filter = useAppSelector((state) => state.filter);
  const [amenities, setAmenities] = useOptimisticReducer(filter.amenities);
  const [laundry, setLaundry] = useOptimisticReducer(filter.laundry);
  const [pets, setPets] = useOptimisticReducer(filter.pets);
  const { cooling, heating, parking } = amenities;
  const { inUnit, shared } = laundry;
  const { cats, dogs } = pets;

  const handleLaundryClick = (selection: keyof laundryType) => {
    setLaundry({ type: selection, payload: !laundry[selection] });
    handleFilterClick(selection, 'laundry');
  };

  const handleAmenitiesClick = (selection: keyof amenitiesType) => {
    setAmenities({ type: selection, payload: !amenities[selection] });
    handleFilterClick(selection, 'amenities');
  };

  const handlePetsClick = (selection: keyof petsType) => {
    setPets({ type: selection, payload: !pets[selection] });
    handleFilterClick(selection, 'pets');
  };

  const handleFilterClick = (selection: string, filterType: string) => {
    const newFilter = assign({}, filter[filterType]);
    newFilter[selection] = !filter[filterType][selection];
    onFilterChange({ [filterType]: newFilter });
  };

  const component = (
    <FieldSet
      css={{ width: '100%', paddingBlock: '200' }}
      legend={
        <VisuallyHidden asChild>
          <Legend>Amenities</Legend>
        </VisuallyHidden>
      }
    >
      {/* oxlint-disable-next-line constellation/no-raw-spacing-values -- `minWidth: 320px` is past the end of the numeric sizes scale, which stops at `5000` (200px) */}
      <Box css={{ minWidth: '320px', padding: '{spacing.100} {spacing.600}' }}>
        <LabeledControl
          controlId="amenities-1"
          label={<Label>Shared laundry</Label>}
          control={<Checkbox checked={shared} onChange={() => handleLaundryClick('shared')} />}
        />
      </Box>
      {/* oxlint-disable-next-line constellation/no-raw-spacing-values -- `minWidth: 320px` is past the end of the numeric sizes scale, which stops at `5000` (200px) */}
      <Box css={{ minWidth: '320px', padding: '{spacing.100} {spacing.600}' }}>
        <LabeledControl
          controlId="amenities-2"
          label={<Label>In-unit laundry</Label>}
          control={<Checkbox checked={inUnit} onChange={() => handleLaundryClick('inUnit')} />}
        />
      </Box>
      {/* oxlint-disable-next-line constellation/no-raw-spacing-values -- `minWidth: 320px` is past the end of the numeric sizes scale, which stops at `5000` (200px) */}
      <Box css={{ minWidth: '320px', padding: '{spacing.100} {spacing.600}' }}>
        <LabeledControl
          controlId="amenities-3"
          label={<Label>Parking</Label>}
          control={<Checkbox checked={parking} onChange={() => handleAmenitiesClick('parking')} />}
        />
      </Box>
      {/* oxlint-disable-next-line constellation/no-raw-spacing-values -- `minWidth: 320px` is past the end of the numeric sizes scale, which stops at `5000` (200px) */}
      <Box css={{ minWidth: '320px', padding: '{spacing.100} {spacing.600}' }}>
        <LabeledControl
          controlId="amenities-4"
          label={<Label>Has A/C</Label>}
          control={<Checkbox checked={cooling} onChange={() => handleAmenitiesClick('cooling')} />}
        />
      </Box>
      {/* oxlint-disable-next-line constellation/no-raw-spacing-values -- `minWidth: 320px` is past the end of the numeric sizes scale, which stops at `5000` (200px) */}
      <Box css={{ minWidth: '320px', padding: '{spacing.100} {spacing.600}' }}>
        <LabeledControl
          controlId="amenities-5"
          label={<Label>Has heating</Label>}
          control={<Checkbox checked={heating} onChange={() => handleAmenitiesClick('heating')} />}
        />
      </Box>
      {/* oxlint-disable-next-line constellation/no-raw-spacing-values -- `minWidth: 320px` is past the end of the numeric sizes scale, which stops at `5000` (200px) */}
      <Box css={{ minWidth: '320px', padding: '{spacing.100} {spacing.600}' }}>
        <LabeledControl
          controlId="amenities-6"
          label={<Label>Allows cats</Label>}
          control={<Checkbox checked={cats} onChange={() => handlePetsClick('cats')} />}
        />
      </Box>
      {/* oxlint-disable-next-line constellation/no-raw-spacing-values -- `minWidth: 320px` is past the end of the numeric sizes scale, which stops at `5000` (200px) */}
      <Box css={{ minWidth: '320px', padding: '{spacing.100} {spacing.600}' }}>
        <LabeledControl
          controlId="amenities-7"
          label={<Label>Allows dogs</Label>}
          control={<Checkbox checked={dogs} onChange={() => handlePetsClick('dogs')} />}
        />
      </Box>
    </FieldSet>
  );

  return FilterWrapper({
    heading: 'Amenities',
    children: component,
  });
};

export default Amenities;
